import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AvatarImage } from './avatar-image'
import { useAuth } from '../contexts/auth-context'
import { usePresenceHidden, useResolvedLastActiveAt, useResolvedPresence } from '../stores/presence-store'
import {
  clearPendingCallGate,
  usePendingCallGate,
} from '../stores/call-gate-store'
import {
  resolveSelfUserId,
  getOrCreateDirectConversation,
  sendSystemMessage,
} from '../services/chat-service'
import { primaryCallEligibilityIssue } from '@shared/calls/call-eligibility'
import { encodeCallbackRequestBody } from '@shared/calls/call-system-message'
import { presenceStatusLabel } from '../utils/presence'

function ChatIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z" />
    </svg>
  )
}

/**
 * Top-level dialog shown when a user tries to call someone whose status warrants
 * a warning (idle / busy → "Call anyway") or a block (offline / DND / out →
 * messaging only). Mounted once near the call overlays.
 */
export function CallGateDialog() {
  const gate = usePendingCallGate()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [openingChat, setOpeningChat] = useState(false)
  const [requestingCallback, setRequestingCallback] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const peer = gate?.peer
  const presence = useResolvedPresence(peer)
  const lastActiveAt = useResolvedLastActiveAt(peer)
  const presenceHidden = usePresenceHidden(peer?.userId)

  useEffect(() => {
    setError(null)
    setOpeningChat(false)
    setRequestingCallback(false)
  }, [gate])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearPendingCallGate()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  if (!gate || !peer) return null

  const blocked = gate.result.severity === 'block'
  const primaryIssue = primaryCallEligibilityIssue(gate.result)
  const headlineForIssue = (): string => {
    if (blocked) return `You can't call ${peer.displayName} right now`
    switch (primaryIssue?.code) {
      case 'target_calendar_unavailable':
        return `Outside ${peer.displayName}'s availability`
      case 'target_pause_notifications':
        return `${peer.displayName} has do-not-disturb on`
      case 'target_on_active_call':
        return `${peer.displayName} is on another call`
      default:
        return `Contact ${peer.displayName} anyway?`
    }
  }
  const headline = headlineForIssue()
  const detail =
    primaryIssue?.message ??
    (blocked
      ? `${peer.displayName} is unavailable.`
      : `${peer.displayName} may not answer right away.`)

  const handleMessage = async () => {
    if (openingChat) return
    const actorId = resolveSelfUserId(user?.userId)
    if (!actorId) {
      navigate('/messages/direct')
      clearPendingCallGate()
      return
    }
    setOpeningChat(true)
    setError(null)
    try {
      const thread = await getOrCreateDirectConversation(peer.userId, actorId)
      clearPendingCallGate()
      navigate(`/messages/direct?thread=${encodeURIComponent(thread.id)}`)
    } catch {
      // Fall back to the direct inbox so the user can still reach messaging.
      clearPendingCallGate()
      navigate('/messages/direct')
    }
  }

  const handleCallAnyway = () => {
    const proceed = gate.proceed
    clearPendingCallGate()
    proceed()
  }

  // Posts a lightweight, in-thread "please call me back" SYSTEM message into the
  // DM (distinct from the Sessions "book a callback session" feature). Uses the
  // normal message-send path so both participants get it via subscription — no
  // polling/refetch loop.
  const handleRequestCallback = async () => {
    if (requestingCallback) return
    const actorId = resolveSelfUserId(user?.userId)
    const requesterName = user?.displayName?.trim() || 'Someone'
    if (!actorId) {
      navigate('/messages/direct')
      clearPendingCallGate()
      return
    }
    setRequestingCallback(true)
    setError(null)
    try {
      const thread =
        gate.conversationId
          ? { id: gate.conversationId }
          : await getOrCreateDirectConversation(peer.userId, actorId)
      await sendSystemMessage(
        thread.id,
        encodeCallbackRequestBody({ requesterId: actorId, requesterName }),
      )
      clearPendingCallGate()
      navigate(`/messages/direct?thread=${encodeURIComponent(thread.id)}`)
    } catch {
      setRequestingCallback(false)
      setError('Could not send your callback request. Please try again.')
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={() => clearPendingCallGate()}
    >
      <div
        className="animate-fade-slide-in w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-gate-title"
        aria-describedby="call-gate-detail"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center px-6 pb-2 pt-6 text-center">
          <AvatarImage
            name={peer.displayName}
            avatarUrl={peer.avatarUrl}
            size={72}
            variant="provider"
            presence={presence}
          />
          <h2 id="call-gate-title" className="mt-3 text-lg font-bold text-slate-900">
            {headline}
          </h2>
          <p id="call-gate-detail" className="mt-1 text-sm text-slate-600">
            {detail}
          </p>
          {!presenceHidden && (
            <p className="mt-1 text-xs text-slate-400 first-letter:uppercase">
              {presenceStatusLabel(presence, lastActiveAt)}
            </p>
          )}
        </div>

        <div className="space-y-2 p-4">
          <button
            type="button"
            onClick={() => void handleMessage()}
            disabled={openingChat}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
          >
            <ChatIcon />
            {openingChat ? 'Opening…' : 'Send a message'}
          </button>

          {blocked && (
            <button
              type="button"
              onClick={() => void handleRequestCallback()}
              disabled={requestingCallback}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-200 bg-brand-50 py-2.5 text-sm font-semibold text-brand-700 transition hover:bg-brand-100 disabled:opacity-60"
            >
              <PhoneIcon />
              {requestingCallback ? 'Requesting…' : 'Request a callback'}
            </button>
          )}

          {!blocked && (
            <button
              type="button"
              onClick={handleCallAnyway}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-200 bg-brand-50 py-2.5 text-sm font-semibold text-brand-700 transition hover:bg-brand-100"
            >
              <PhoneIcon />
              Call anyway
            </button>
          )}

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={() => clearPendingCallGate()}
            className="w-full rounded-xl py-2.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
